import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Sparkles, CheckCircle2, Volume2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * TechniqueDetail — /techniques/:id
 *
 * Reads a static technique from /api/techniques/{id} (Pro-gated) and
 * renders an opinionated tutorial layout:
 *   - Hero (title, vibe, difficulty, time)
 *   - Trouvé's affirming intro
 *   - Numbered step cards with title, where (anatomical placement),
 *     what (the actual technique), optional tip
 *   - "Start with AR mirror" CTA (handoff to LiveCoach, pre-loaded
 *     with these technique steps)
 *
 * The backend returns 402 with paywall payload for free users — we
 * catch that and redirect to /pricing with the right reason context.
 */

const DIFFICULTY_COPY = {
  beginner: "Beginner",
  intermediate: "Intermediate",
  advanced: "Advanced",
};

const TechniqueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [technique, setTechnique] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(
          `${API}/techniques/${id}`,
          { withCredentials: true },
        );
        setTechnique(data.technique);
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 402) {
          // Paywall — bounce to pricing with reason context
          navigate("/pricing", {
            state: {
              reason: detail?.reason || "technique_locked",
              techniqueId: id,
            },
          });
          return;
        }
        if (status === 404) {
          toast.error("That technique doesn't exist anymore.");
          navigate("/techniques");
          return;
        }
        toast.error("Couldn't load this technique.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen trubeaute-gradient">
        <Header />
        <div className="flex items-center justify-center pt-40">
          <div className="h-8 w-8 border-2 border-[#9B6A6C] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }
  if (!technique) return null;

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <Header />
      <section className="relative pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate("/techniques")}
            className="inline-flex items-center gap-2 text-[#9B6A6C] hover:text-[#805557] text-sm mb-8 transition-colors"
            data-testid="technique-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            All techniques
          </button>

          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <div className="text-6xl mb-4" aria-hidden="true">
              {technique.hero_emoji}
            </div>
            <h1 className="font-serif italic text-4xl sm:text-5xl text-[#2C2123] mb-3 leading-[1.1]">
              {technique.title}
            </h1>
            <p className="text-[#9B6A6C] text-lg mb-4">{technique.vibe}</p>
            <div className="flex items-center gap-3 flex-wrap text-xs">
              <span className="px-3 py-1.5 rounded-full bg-[#F2E8E9] text-[#5D4050] uppercase tracking-wider">
                {DIFFICULTY_COPY[technique.difficulty] || technique.difficulty}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F5EFEB] text-[#5C4B51]">
                <Clock className="h-3 w-3" />
                {technique.total_minutes} min
              </span>
              <span className="px-3 py-1.5 rounded-full bg-[#F8EEEE] text-[#5D4050]">
                {technique.steps.length} steps
              </span>
            </div>
          </motion.div>

          {/* Trouvé's intro */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-[#EADDD7] rounded-2xl p-6 mb-10 soft-shadow"
          >
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center shrink-0">
                <Volume2 className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#9B6A6C] mb-1">
                  Trouvé says
                </p>
                <p className="font-serif italic text-lg text-[#2C2123] leading-relaxed">
                  "{technique.intro}"
                </p>
              </div>
            </div>
          </motion.div>

          {/* Steps */}
          <div className="space-y-4 mb-12">
            {technique.steps.map((step, i) => (
              <StepCard key={i} index={i} step={step} />
            ))}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#9B6A6C] to-[#805557] rounded-3xl p-8 text-center shadow-[0_12px_36px_rgba(155,106,108,0.35)]"
          >
            <h3 className="text-white font-serif italic text-2xl mb-2">
              Practice with the AR mirror
            </h3>
            <p className="text-white/85 text-sm leading-relaxed mb-6 max-w-md mx-auto">
              Trouvé walks you through every step. Voice + visual
              overlay on your real face — works with your phone camera.
            </p>
            <button
              onClick={() => {
                // The AR mirror lives inside the Routine page, which
                // accepts a `technique:{slug}` id to load this content
                // as a mock routine (no DB write). This is the route
                // we should have used from the start — the earlier
                // `/mirror` target didn't exist and silently failed.
                navigate(`/routine/technique:${technique.id}`);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#9B6A6C] rounded-full font-medium hover:bg-[#FDFBF7] transition-all"
              data-testid="technique-start-mirror-btn"
            >
              <Sparkles className="h-4 w-4" />
              Start the technique
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const StepCard = ({ index, step }) => (
  <motion.div
    initial={{ opacity: 0, x: -8 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: 0.15 + index * 0.05 }}
    className="bg-white border border-[#EADDD7] rounded-2xl p-6 soft-shadow"
    data-testid={`technique-step-${index + 1}`}
  >
    <div className="flex items-start gap-4">
      <div className="h-9 w-9 rounded-full bg-[#F8EEEE] flex items-center justify-center shrink-0 mt-1">
        <span className="text-[#9B6A6C] font-medium text-sm">{index + 1}</span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[#2C2123] font-medium text-base mb-2">
          {step.title}
        </h3>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#9B6A6C] mb-2 inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-3 w-3" />
          {step.where}
        </div>
        <p className="text-[#5C4B51] text-sm leading-relaxed mb-3">
          {step.what}
        </p>
        {step.tip && (
          <div className="mt-3 pl-3 border-l-2 border-[#D5A8A4] text-[#9B6A6C] text-xs italic leading-relaxed">
            <strong className="not-italic">Tip:</strong> {step.tip}
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

export default TechniqueDetail;
